"use client";

import { useState, useEffect, useRef, memo, useCallback } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  Icon20ArrowRightOutline,
  Icon20SparkleOutline,
  Icon20CancelCircleOutline,
} from "@linqx/icons/web";
import { Loader2 } from "lucide-react";
import { Language } from "@linqx/i18n";
import { Input } from "@/components/ui/input";
import { BlurFade } from "@/components/magicui/blur-fade";
import { BlurText } from "@/components/reactbits/blur-text";
import { Orb } from "@/components/reactbits/orb";
import { Confetti } from "@/components/reactbits/confetti";
import { Reveal } from "@/components/motion/scroll-fx";
import { cn } from "@/lib/utils";
import { initLandingApi } from "@/lib/api";
import { getWaitlistApi } from "@linqx/api-client/apis/waitlist";
import { ResponseError } from "@linqx/api-client/generated";
import { Telemetry, TelemetryEvent } from "@linqx/telemetry";

// Where these waitlist signups originate — sent as the referral source to both
// the backend and PostHog so funnel attribution stays consistent.
const REFERRAL_SOURCE = "landing-page";

// Memoized Orb to prevent re-renders on form state changes
const MemoizedOrb = memo(function MemoizedOrb() {
  return (
    <Orb
      hue={0}
      hoverIntensity={0.15}
      forceHoverState={true}
      backgroundColor="#FAF5F5"
    />
  );
});

// Memoized section header to prevent re-renders
const SectionHeader = memo(function SectionHeader() {
  return (
    <Reveal direction="up" distance={56} exit={false}>
      <BlurFade delay={0.1} inView>
        <div className="text-center mb-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-brand-200 shadow-sm mb-6"
          >
            <Icon20SparkleOutline
              width={16}
              height={16}
              className="text-brand-500"
            />
            <span className="text-sm font-medium text-brand-800">
              {Language.getKey("landing_waitlist_badge")}
            </span>
          </motion.div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-3">
            {Language.getKey("landing_waitlist_title_prefix")}
            <span className="text-foreground">
              {Language.getKey("landing_waitlist_title_highlight")}
            </span>
          </h2>
          <p className="text-base text-muted-foreground">
            {Language.getKey("landing_waitlist_subtitle")}
          </p>
        </div>
      </BlurFade>
    </Reveal>
  );
});

// Toast notification component
const ErrorToast = memo(function ErrorToast({
  message,
  onClose,
}: {
  message: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 80, scale: 0.3, rotateX: 45 }}
      animate={{
        opacity: 1,
        y: 0,
        scale: [0.3, 1.1, 0.95, 1],
        rotateX: [45, -10, 5, 0],
      }}
      exit={{
        opacity: 0,
        y: 40,
        scale: 0.8,
        transition: { duration: 0.2, ease: "easeIn" },
      }}
      transition={{
        duration: 0.5,
        ease: [0.34, 1.56, 0.64, 1],
        scale: { duration: 0.6, times: [0, 0.4, 0.7, 1] },
        rotateX: { duration: 0.6, times: [0, 0.4, 0.7, 1] },
      }}
      style={{ perspective: 1000 }}
      className="fixed bottom-6 left-0 right-0 mx-auto z-50 px-5 py-3 rounded-xl bg-white border border-red-200 shadow-lg shadow-red-100/50 flex items-center gap-3 w-fit"
    >
      <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
        <Icon20CancelCircleOutline
          className="text-red-500"
          width={18}
          height={18}
        />
      </div>
      <p className="text-sm text-red-700 font-medium">{message}</p>
    </motion.div>
  );
});

// Flip card variants
const flipVariants = {
  front: {
    rotateY: 0,
    opacity: 1,
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
  },
  back: {
    rotateY: 180,
    opacity: 0,
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
  },
};

// Shake animation for form errors
const shakeVariants = {
  shake: {
    x: [0, -12, 12, -12, 12, -6, 6, 0],
    transition: { duration: 0.5, ease: "easeInOut" },
  },
};

interface WaitlistFormProps {
  className?: string;
}

export function WaitlistForm({ className }: WaitlistFormProps) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);

  // Track when the waitlist form first scrolls into view (fires once).
  const sectionRef = useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.4 });

  // Initialize API client on mount
  useEffect(() => {
    initLandingApi();
  }, []);

  // Fire a single form-viewed event the first time the section is visible.
  useEffect(() => {
    if (inView) {
      Telemetry.track(TelemetryEvent.WAITLIST_FORM_VIEWED, {
        referralSource: REFERRAL_SOURCE,
      });
    }
  }, [inView]);

  // Trigger confetti when success - sync with text fade in
  useEffect(() => {
    if (isSuccess) {
      // Trigger when text starts animating (after flip completes)
      const timer = setTimeout(() => {
        setShowConfetti(true);
      }, 450);
      return () => clearTimeout(timer);
    }
  }, [isSuccess]);

  // Trigger error with shake animation
  const triggerError = useCallback((message: string) => {
    setError(message);
    setShouldShake(true);
    // Reset shake after animation completes
    setTimeout(() => setShouldShake(false), 500);
  }, []);

  // Dismiss error toast
  const dismissError = useCallback(() => {
    setError(null);
  }, []);

  // Memoized handlers to prevent re-renders
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setName(e.target.value);
    },
    [],
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setEmail(value);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!email) {
        triggerError(Language.getKey("landing_waitlist_error_email_required"));
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        triggerError(Language.getKey("landing_waitlist_error_email_invalid"));
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const api = getWaitlistApi();
        await api.waitlistControllerJoinWaitlist({
          joinWaitlistDto: {
            email,
            name: name || undefined,
            referralSource: REFERRAL_SOURCE,
          },
        });

        Telemetry.track(TelemetryEvent.WAITLIST_SUBMITTED, {
          referralSource: REFERRAL_SOURCE,
          hasName: Boolean(name),
        });

        setIsSuccess(true);
      } catch (err) {
        let message = Language.getKey("landing_waitlist_error_generic");
        if (err instanceof ResponseError) {
          try {
            const errorBody = await err.response.json();
            message = errorBody.message || message;
          } catch {
            // Ignore JSON parse error
          }
        } else if (err instanceof Error) {
          message = err.message;
        }
        triggerError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [email, name, triggerError],
  );

  return (
    <section
      ref={sectionRef}
      id="waitlist"
      className={cn(
        "py-24 md:py-32 relative overflow-hidden bg-brand-50",
        className,
      )}
    >
      {/* Error toast */}
      <AnimatePresence>
        {error && <ErrorToast message={error} onClose={dismissError} />}
      </AnimatePresence>

      {/* Orb background - memoized to prevent re-renders */}
      <MemoizedOrb />

      {/* Confetti overlay - covers whole section */}
      <Confetti isActive={showConfetti} particleCount={100} mode="burst" />

      {/* Top gradient fade */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-background to-transparent pointer-events-none z-[1]" />
      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none z-[1]" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-lg mx-auto">
          {/* Section Header - memoized */}
          <SectionHeader />

          {/* Form Card with flip effect */}
          <BlurFade delay={0.2} inView>
            <div className="relative" style={{ perspective: "1000px" }}>
              <AnimatePresence mode="wait">
                {!isSuccess ? (
                  <motion.div
                    key="form"
                    variants={{ ...flipVariants, ...shakeVariants }}
                    initial="front"
                    exit="back"
                    animate={shouldShake ? "shake" : "front"}
                    className="p-6 md:p-8 rounded-2xl bg-white/90 backdrop-blur-md border border-brand-100 shadow-xl"
                    style={{
                      backfaceVisibility: "hidden",
                      transformStyle: "preserve-3d",
                    }}
                  >
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <Input
                          type="text"
                          placeholder={Language.getKey(
                            "landing_waitlist_name_placeholder",
                          )}
                          value={name}
                          onChange={handleNameChange}
                          className="h-12 bg-white border-border/50 focus:border-foreground/50 rounded-xl transition-colors duration-200"
                        />
                      </div>

                      <div>
                        <Input
                          type="email"
                          placeholder={Language.getKey(
                            "landing_waitlist_email_placeholder",
                          )}
                          value={email}
                          onChange={handleEmailChange}
                          required
                          className="h-12 bg-white border-border/50 focus:border-foreground/50 rounded-xl transition-colors duration-200"
                        />
                      </div>

                      <motion.button
                        type="submit"
                        disabled={isLoading}
                        whileHover={
                          !isLoading ? { scale: 1.02, y: -2 } : undefined
                        }
                        whileTap={!isLoading ? { scale: 0.98 } : undefined}
                        className="btn-brand w-full h-12 text-base font-semibold rounded-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-shadow hover:shadow-lg"
                      >
                        <AnimatePresence mode="wait">
                          {isLoading ? (
                            <motion.div
                              key="loading"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              transition={{ duration: 0.15 }}
                            >
                              <Loader2 className="h-5 w-5 animate-spin" />
                            </motion.div>
                          ) : (
                            <motion.span
                              key="text"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.15 }}
                              className="flex items-center gap-2"
                            >
                              {Language.getKey("landing_waitlist_submit")}
                              <Icon20ArrowRightOutline />
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </motion.button>

                      <p className="text-xs text-muted-foreground text-center">
                        {Language.getKey("landing_waitlist_disclaimer")}
                      </p>
                    </form>
                  </motion.div>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      delay: 0.3,
                      duration: 0.5,
                      ease: [0.34, 1.56, 0.64, 1],
                    }}
                    className="p-6 md:p-8"
                  >
                    <div className="text-center py-6">
                      {/* Main animated text */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.5, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{
                          delay: 0.4,
                          duration: 0.6,
                          ease: [0.34, 1.56, 0.64, 1],
                        }}
                        className="mb-4"
                      >
                        <BlurText
                          text={Language.getKey(
                            "landing_waitlist_success_title",
                          )}
                          className="text-3xl md:text-4xl font-heading font-bold text-brand-900"
                          delay={60}
                          animateBy="letters"
                          direction="top"
                        />
                      </motion.div>

                      {/* Subtitle with staggered animation */}
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.4 }}
                        className="text-lg text-muted-foreground mb-2"
                      >
                        {Language.getKey("landing_waitlist_success_subtitle")}
                      </motion.p>

                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.4 }}
                        className="text-sm text-brand-600"
                      >
                        {Language.getKey("landing_waitlist_success_note")}
                      </motion.p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </BlurFade>
        </div>
      </div>
    </section>
  );
}
